import React, { useMemo, useState } from 'react';
import { Progress } from '@backstage/core-components';
import { Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { AccessDashboardColumn } from './AccessDashboardColumn';
import { mapMyTeamRoles } from './mapMyTeamRoles';
import { RequestAccessModal } from './RequestAccessModal/RequestAccessModal';
import {
  RequestAccessFormData,
  toRoleAccessRequestDuration,
} from './RequestAccessModal/constants';
import { DIAM_TEAM_ID } from '../../diamApi';
import { DashboardApplicationGroup, DashboardRole } from './types';
import { useMyTeamRoles } from './useMyTeamRoles';
import { useRequestRoleAccess } from './useRequestRoleAccess';
import { DashboardLayout } from './styles';

function firstRoleId(groups: DashboardApplicationGroup[]): string | undefined {
  return groups[0]?.roles[0]?.id;
}

export function AccessDashboard() {
  const { enqueueSnackbar } = useSnackbar();
  const rolesQuery = useMyTeamRoles();
  const requestAccess = useRequestRoleAccess();
  const [roleToRequest, setRoleToRequest] = useState<DashboardRole | null>(
    null,
  );
  const [pendingRequestRoleIds, setPendingRequestRoleIds] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const dashboard = useMemo(
    () => (rolesQuery.data ? mapMyTeamRoles(rolesQuery.data) : null),
    [rolesQuery.data],
  );

  const handleApplyRequest = async (
    role: DashboardRole,
    data: RequestAccessFormData,
  ) => {
    try {
      await requestAccess.mutateAsync({
        teamId: DIAM_TEAM_ID,
        roleId: role.id,
        justification: data.justification,
        duration: toRoleAccessRequestDuration(data.duration),
      });
      setPendingRequestRoleIds(current => new Set(current).add(role.id));
      enqueueSnackbar(`Access request submitted for ${role.name}`, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error, 'Failed to submit access request'),
        { variant: 'error' },
      );
    }
  };

  if (rolesQuery.isPending) {
    return <Progress />;
  }

  if (rolesQuery.isError || !dashboard) {
    return (
      <Typography>
        {getApiErrorMessage(
          rolesQuery.error,
          'Failed to load your roles for this team.',
        )}
      </Typography>
    );
  }

  return (
    <>
      <DashboardLayout>
        <AccessDashboardColumn
          kind="granted"
          title="Roles you have"
          subtitle="Access granted per application"
          groups={dashboard.granted}
          initialExpandedRoleId={firstRoleId(dashboard.granted)}
        />
        <AccessDashboardColumn
          kind="available"
          title="Available to request"
          subtitle="Roles you can request per application"
          groups={dashboard.available}
          initialExpandedRoleId={firstRoleId(dashboard.available)}
          pendingRequestRoleIds={pendingRequestRoleIds}
          onRequestAccess={setRoleToRequest}
        />
      </DashboardLayout>
      <RequestAccessModal
        isOpen={Boolean(roleToRequest)}
        role={roleToRequest}
        onClose={() => setRoleToRequest(null)}
        onApply={handleApplyRequest}
      />
    </>
  );
}
